import Button from "@components/common/Button";
import Input from "@components/common/Input";
import SelectInputClient from "@components/common/SelectInputClient";
import DeleteIcon from "@components/icons/DeleteIcon";
import InfoCircleIcon from "@components/icons/InfoCircleIcon";
import PlusIcon from "@components/icons/PlusIcon";
import VoucherIcon from "@components/icons/VoucherIcon";
import ModalAlert from "@components/Modals/ModalAlert";
import { useIsMobile } from "@components/mediaQuery/useIsMobile";
import { fetchVessels } from "@services/vesselService";
import { createVoucherRequest } from "@services/voucherConfigService";
import { Modal, Tooltip } from "flowbite-react";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { customModalTheme } from "styles/customTheme";

const useFetchVessels = (
	searchTerm,
	pageVessel,
	token,
	dispatch,
	navigate,
	setLoader,
	setHasMore,
	setFilteredVessels, // renamed to match what's actually passed in
) => {
	useEffect(() => {
		const controller = new AbortController();
		const signal = controller.signal;

		const fetchdataVessels = debounce(() => {
			if (token && pageVessel > 0) {
				setLoader(true);

				fetchVessels(
					token,
					dispatch,
					navigate,
					pageVessel,
					10,
					1, // paginate = true
					{ siteName: searchTerm }, // searchTerms
					null,
					null,
					signal,
				)
					.then((data) => {
						if (!data) return;

						const totalPage = Math.ceil(data.totalItems / data.perPage);

						setFilteredVessels((prev) => (pageVessel === 1 ? data.data : [...prev, ...data.data]));
						setHasMore(totalPage > pageVessel);
					})
					.catch((error) => console.error("Failed to fetch vessels:", error))
					.finally(() => setLoader(false));
			}
		}, 300);
		fetchdataVessels();
		return () => {
			controller.abort();
			fetchdataVessels.cancel();
		};
	}, [
		searchTerm,
		pageVessel,
		token,
		dispatch,
		navigate,
		setHasMore,
		setLoader,
		setFilteredVessels,
	]);
};
const MapPinIcon = () => (
	<svg
		className="h-4 w-4 text-blue-gray md:h-5 md:w-5"
		fill="none"
		viewBox="0 0 24 24"
		stroke="currentColor"
		strokeWidth={2}
	>
		<path
			strokeLinecap="round"
			strokeLinejoin="round"
			d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z"
		/>
		<path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
	</svg>
);

export const VouchersConfigModal = ({ id, isOpen, closeModal }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const token = useSelector((state) => state.auth.user.token);
	const isMobile = useIsMobile();

	const [vesselSite, setVesselSite] = useState("");
	const [vesselSiteName, setVesselSiteName] = useState("");
	// voucherLevels is now an array of level objects instead of a single number
	const [voucherLevels, setVoucherLevels] = useState([]);
	const [notes, setNotes] = useState("");

	const [loadingSubmit, setLoadingSubmit] = useState(false);
	const [errors, setErrors] = useState({
		vesselSite: "",
		voucherLevels: [],
		voucherLevelsGeneral: "",
	});

	const [showErrorModal, setShowErrorModal] = useState(false);
	const [errorMessage, setErrorMessage] = useState(false);
	const [successMessage, setSuccessMessage] = useState(false);
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [pageVessel, setPageVessel] = useState(1);
	const [searchTerm, setSearchTerm] = useState("");
	const [loaderVessel, setLoaderVessel] = useState(false);
	const [hasMoreVessel, setHasMoreVessel] = useState(true);
	const [filteredVessels, setFilteredVessels] = useState([]);

	useFetchVessels(
		searchTerm,
		pageVessel,
		token,
		dispatch,
		navigate,
		setLoaderVessel,
		setHasMoreVessel,
		setFilteredVessels,
	);

	const handleVesselChange = (fieldId, value) => {
		setVesselSite(value);
		const selected = filteredVessels.find((v) => v.id === value);
		setVesselSiteName(selected?.site_name || "");
		setErrors((prev) => ({ ...prev, vesselSite: "" }));
	};
	const handleSearchSelectChange = useCallback((fieldId, value) => {
		setPageVessel(1);
		setSearchTerm(value);
	}, []);

	const handleAddVoucherLevel = () => {
		setVoucherLevels((prev) => [
			...prev,
			{ id: crypto.randomUUID(), name: "", allowance: "", count: "", validity: "" },
		]);
		setErrors((prev) => ({ ...prev, voucherLevels: "" }));
	};

	const handleRemoveVoucherLevel = (id) => {
		setVoucherLevels((prev) => prev.filter((level) => level.id !== id));
	};

	const handleVoucherLevelChange = (id, field, value) => {
		setVoucherLevels((prev) =>
			prev.map((level) => (level.id === id ? { ...level, [field]: value } : level)),
		);
		setErrors((prev) => ({
			...prev,
			[`voucherLevel_${id}_${field}`]: "",
		}));
	};

	console.log("voucherlevel", voucherLevels);

	const handleVoucherLevel = (value) => {
		setVoucherLevels(value);
		setErrors((prev) => ({
			...prev,
			voucherLevels: "",
		}));
	};

	const getLevelError = (levelId) => {
		if (!Array.isArray(errors.voucherLevels)) return {};
		return errors.voucherLevels.find((e) => e.id === levelId) || {};
	};
	const validateForm = () => {
		const newErrors = { vesselSite: "", voucherLevels: [] };
		let isValid = true;

		if (!vesselSite) {
			newErrors.vesselSite = "Please select a vessel/site.";
			isValid = false;
		}

		if (!voucherLevels) {
			newErrors.voucherLevels = "Vouche Level is required.";
		}
		if (voucherLevels.length === 0) {
			newErrors.voucherLevelsGeneral = "At least one voucher level is required.";
			isValid = false;
		}
		newErrors.voucherLevels = voucherLevels.map((level) => {
			const levelErrors = { id: level.id };

			if (!level.name?.trim()) {
				levelErrors.name = "Name is required.";
				isValid = false;
			}
			if (!level.allowance?.trim()) {
				levelErrors.allowance = "Allowance is required.";
				isValid = false;
			}
			if (!level.count || Number(level.count) <= 0) {
				levelErrors.count = "Count must be greater than 0.";
				isValid = false;
			}
			if (!level.validity || Number(level.validity) <= 0) {
				levelErrors.validity = "Validity is required.";
				isValid = false;
			}

			return levelErrors;
		});

		setErrors(newErrors);
		return isValid;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!validateForm()) return;

		const data = {
			vessel_id: vesselSite,
			vessel_name: vesselSiteName,
			voucher_levels: voucherLevels.map((level) => ({
				name: level.name,
				monthly_allowance: level.allowance,
				count: Number(level.count),
				validity_days: Number(level.validity),
			})),
		};
		if (notes) {
			data.description = notes;
		}

		try {
			setLoadingSubmit(true);

			const response = await createVoucherRequest(data, token);

			setSuccessMessage(response.message);
			setShowSuccessModal(true);
			setTimeout(() => {
				closeModal();
			}, 1000);
		} catch (err) {
			setErrorMessage(
				err?.message || Object.values(err?.errors || {})?.[0]?.[0] || "Something went wrong",
			);
			setShowErrorModal(true);
		} finally {
			setLoadingSubmit(false);
		}
	};

	return (
		<Modal
			id={"voucherRequest"}
			show={isOpen}
			onClose={closeModal}
			size="2xl"
			theme={customModalTheme}
		>
			<Modal.Header className="border-[#272B49] border-b bg-[#11193F]">
				<div className="flex w-full items-center justify-between">
					<div className="flex flex-col items-start gap-1">
						<h2 className="break-words font-semibold text-sm text-white sm:text-lg">
							Service Activation Request
						</h2>

						<span className="font-normal text-blue-gray text-xs sm:text-sm">
							Select a vessel or site and configure voucher levels
						</span>
					</div>
				</div>
			</Modal.Header>
			<Modal.Body className="overflow-y-auto px-4 md:px-6">
				<div className="space-y-4">
					{/* Vessel / Site */}
					<div className="min-w-0">
						<label className="mb-2 flex items-center gap-2 text-left font-semibold text-sm text-white">
							<MapPinIcon />
							Vessel / Site
						</label>
						<div className="p-1">
							<SelectInputClient
								id="vesselSelect"
								placeholder="Select Vessel or Site"
								label="Select Vessel or Site"
								value={vesselSite}
								onChange={handleVesselChange}
								options={filteredVessels}
								displayField="site_name"
								search={handleSearchSelectChange}
								fetchMoreData={() => {
									setPageVessel((prev) => prev + 1);
								}}
								hasMore={hasMoreVessel}
								loader={loaderVessel}
								marginTop={`${isMobile ? "mt-16" : "mt-20"}`}
								classDropdown={`${isMobile ? "absolute z-20" : "absolute z-40"}`}
							/>
						</div>

						{errors.vesselSite && <p className="mt-1 text-red-500 text-xs">{errors.vesselSite}</p>}
					</div>

					{/* Voucher Levels */}
					<div className="min-w-0">
						<label className="mb-1 flex items-center gap-2 text-left font-semibold text-sm text-white">
							<VoucherIcon className="h-4 w-4 text-blue-gray md:h-5 md:w-5" />
							Voucher Levels
						</label>
						<p className="p-1 text-gray-400 text-xs">
							Define the voucher levels for IEC Social service activation. Each level represents a
							different tier with its own data allowance.
						</p>
						<div className="mx-1">
							<button
								type="button"
								onClick={handleAddVoucherLevel}
								className="mt-2 mb-3 flex items-start justify-center gap-2 rounded-lg border border-gray-600 border-dashed p-2 font-semibold text-gray-300 text-xs transition-colors hover:border-[#7A82D8] hover:text-[#7A82D8]"
							>
								<PlusIcon className="h-4 w-4" />
								Add Voucher Level
							</button>
						</div>

						<div className="grid gap-3">
							{voucherLevels.map((level, index) => {
								const levelError = getLevelError(level.id);

								return (
									<div
										key={level.id}
										className="rounded-lg border border-gray-600 bg-dark-blue/60 p-2"
									>
										<div className="mb-2 flex items-end justify-end">
											<button
												type="button"
												onClick={() => handleRemoveVoucherLevel(level.id)}
												className="text-red-500 hover:text-red-400"
												aria-label="Remove voucher level"
											>
												<Tooltip
													animation="duration-150"
													content="Remove voucher level"
													className="!text-xs !p-1 bg-dark-blue bg-opacity-80"
												>
													<DeleteIcon className="h-4 w-4 text-blue-gray md:h-5 md:w-5" />{" "}
												</Tooltip>
											</button>
										</div>

										<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
											<div className="min-w-0">
												<label className="mb-1 block text-gray-400 text-xs">
													Voucher Name (Level)
												</label>
												<Input
													id={`voucherName-${level.id}`}
													type="text"
													placeholder="Voucher Name"
													value={level.name || ""}
													onChange={(value) => handleVoucherLevelChange(level.id, "name", value)}
													onClear={() => handleVoucherLevelChange(level.id, "name", "")}
													className={`input-bg block w-auto truncate rounded-lg border border-gray-600 py-3 text-gray-400 text-xs placeholder-gray-400 focus:border-blue-gray focus:ring-blue-gray ${
														errors[`voucherLevel_${level.id}_name`] ? "border-red-500" : ""
													}`}
													required
												/>
												{levelError.name && (
													<p className="mt-1 text-[10px] text-red-500">{levelError.name}</p>
												)}
											</div>

											<div className="min-w-0">
												<label className="mb-1 block text-gray-400 text-xs">
													Monthly Allowance
												</label>
												<Input
													id={`voucherAllowance-${level.id}`}
													type="text"
													placeholder="Allowance"
													value={level.allowance || ""}
													onChange={(value) =>
														handleVoucherLevelChange(level.id, "allowance", value)
													}
													onClear={() => handleVoucherLevelChange(level.id, "allowance", "")}
													className={`input-bg block w-auto truncate rounded-lg border border-gray-600 py-3 text-gray-400 text-xs placeholder-gray-400 focus:border-blue-gray focus:ring-blue-gray ${
														errors[`voucherLevel_${level.id}_allowance`] ? "border-red-500" : ""
													}`}
													required
												/>
												{levelError.allowance && (
													<p className="mt-1 text-[10px] text-red-500">{levelError.allowance}</p>
												)}
											</div>

											<div className="min-w-0">
												<label className="mb-1 block text-gray-400 text-xs">Count</label>
												<Input
													id={`voucherCount-${level.id}`}
													type="number"
													inputMode="numeric"
													placeholder="Count"
													value={level.count || ""}
													onChange={(value) => handleVoucherLevelChange(level.id, "count", value)}
													onClear={() => handleVoucherLevelChange(level.id, "count", "")}
													className={`input-bg block w-auto truncate rounded-lg border border-gray-600 py-3 text-gray-400 text-xs placeholder-gray-400 focus:border-blue-gray focus:ring-blue-gray ${
														errors[`voucherLevel_${level.id}_count`] ? "border-red-500" : ""
													}`}
													required
												/>
												{levelError.count && (
													<p className="mt-1 text-[10px] text-red-500">{levelError.count}</p>
												)}
											</div>

											<div className="min-w-0">
												<label className="mb-1 block text-gray-400 text-xs">Validity (Days)</label>
												<Input
													id={`voucherValidity-${level.id}`}
													type="number"
													inputMode="numeric"
													placeholder="Validity"
													value={level.validity || ""}
													onChange={(value) =>
														handleVoucherLevelChange(level.id, "validity", value)
													}
													onClear={() => handleVoucherLevelChange(level.id, "validity", "")}
													className={`input-bg block w-auto truncate rounded-lg border border-gray-600 py-3 text-gray-400 text-xs placeholder-gray-400 focus:border-blue-gray focus:ring-blue-gray ${
														errors[`voucherLevel_${level.id}_validity`] ? "border-red-500" : ""
													}`}
													required
												/>
												{levelError.validity && (
													<p className="mt-1 text-[10px] text-red-500">{levelError.validity}</p>
												)}
											</div>
										</div>
									</div>
								);
							})}
						</div>

						{errors.voucherLevelsGeneral && (
							<p className="mt-2 text-red-500 text-xs">{errors.voucherLevelsGeneral}</p>
						)}
					</div>

					{/* Additional Notes */}
					<div className="min-w-0">
						<label className="mb-2 flex items-center gap-2 text-left font-semibold text-sm text-white">
							<InfoCircleIcon className="h-4 w-4 text-blue-gray md:h-5 md:w-5" />
							Additional Notes
						</label>
						<div className="relative mx-1">
							<textarea
								id="notes"
								placeholder="Any additional requirements or notes for the activation request..."
								value={notes}
								onChange={(e) => setNotes(e.target.value)}
								rows={3}
								className="custom-scrollbar block w-full resize-none rounded-lg border border-gray-600 bg-input-bg py-3 pr-8 text-gray-400 text-xs placeholder-gray-400 focus:border-blue-gray focus:ring-blue-gray"
							/>
							{notes && (
								<div
									className="absolute top-2 right-2 text-gray-400 hover:text-gray-200"
									onClick={() => setNotes("")}
								>
									<svg
										className="h-4 w-4 text-gray-400"
										aria-hidden="true"
										xmlns="http://www.w3.org/2000/svg"
										width="24"
										height="24"
										fill="none"
										viewBox="0 0 24 24"
									>
										<path
											stroke="currentColor"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											d="M6 18 17.94 6M18 18 6.06 6"
										/>
									</svg>
								</div>
							)}
						</div>
					</div>
				</div>
			</Modal.Body>
			<Modal.Footer className="border-color-border bg-modal-bg">
				<Button
					text={loadingSubmit ? "Processing..." : "Submit"}
					onClick={handleSubmit}
					className="!py-2 bg-button-bg text-white hover:bg-button-hover"
					disabled={loadingSubmit}
				/>
				<Button
					text="Cancel"
					color="gray"
					onClick={closeModal}
					className="!py-2 bg-gray-200 text-blue-gray hover:bg-button-cancel-hover hover:text-dark-blue"
				/>
			</Modal.Footer>
			<ModalAlert
				isOpen={showErrorModal}
				type="error"
				closeModal={() => setShowErrorModal(false)}
				message={errorMessage}
			/>

			<ModalAlert
				isOpen={showSuccessModal}
				type="success"
				closeModal={() => setShowSuccessModal(false)}
				message={successMessage}
			/>
		</Modal>
	);
};
